import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

import { contentsApi } from '../../../api/contents';
import { checkoutsApi } from '../../../api/checkouts';
import { paymentsApi } from '../../../api/payments';
import { PageBlocks, useCheckoutPage } from '../components/PageBlocks';
import {
	initBehaviorTracker,
	trackBehavior
} from '../components/PageBlocks/behavior-tracker';
import { isPreviewMode } from '../components/PageBlocks/use-preview-bridge';
import { apiFactory } from '../../../api/ApiFactory';
import '../Thanks/styles.scss';
import './styles.scss';

const { getBySlug } = contentsApi;
const { findByContent } = checkoutsApi;

/**
 * Oferta one-click pós-compra ({platform}/p/upsell/{slug} e /p/downsell/).
 * Upsell aparece quando a compra no cartão foi APROVADA; recusou e há
 * downsell configurado, cai na oferta alternativa antes do obrigado.
 * Aceitar cobra one-click no cartão tokenizado da compra (o token curto vem
 * do close-payment via sessionStorage).
 */
export const UpsellPage = ({
	variant = 'upsell'
}: {
	variant?: 'upsell' | 'downsell';
}) => {
	const { slug } = useParams();
	const [searchParams] = useSearchParams();
	const [status, setStatus] = useState<
		'idle' | 'charging' | 'success' | 'error'
	>('idle');
	const [errorMessage, setErrorMessage] = useState('');

	const { data: content } = useQuery(
		[getBySlug.key, { slug: slug ?? '' }],
		getBySlug.fetch,
		{ refetchOnWindowFocus: false }
	);
	const { data: checkout } = useQuery(
		[findByContent.key, { id: content?.id }],
		findByContent.fetch,
		{ refetchOnWindowFocus: false }
	);
	const page = useCheckoutPage(checkout?.id);
	const upsell = page?.settings?.[variant];
	const downsell = page?.settings?.downsell;

	// Mesma sessão de eventos do checkout (a chave de sessionStorage é por
	// checkoutId) — o funil upsell_shown/accepted/declined emenda no funil
	// da compra na inteligência de conversão.
	useEffect(() => {
		if (!checkout?.id || isPreviewMode()) return;
		apiFactory
			.getInstance()
			.getApiKey()
			.then((apiKey) =>
				initBehaviorTracker({
					checkoutId: checkout.id,
					payUrl: process.env.REACT_APP_CURSEDUCA_PAY_DOMAIN || '',
					apiKey
				})
			)
			.catch(() => undefined);
	}, [checkout?.id]);

	useEffect(() => {
		if (!upsell?.enabled || !upsell?.contentId || isPreviewMode()) return;
		trackBehavior(
			variant === 'downsell' ? 'downsell_shown' : 'upsell_shown',
			undefined,
			String(upsell.contentId),
			{ once: true }
		);
	}, [upsell?.enabled, upsell?.contentId, variant]);

	// Token e query do obrigado gravados pelo checkout no redirect (a chave é
	// por caminho — o desvio pro downsell copia o token pra chave nova).
	const stored = useMemo(() => {
		try {
			return JSON.parse(
				sessionStorage.getItem(
					`checkout-upsell:${window.location.pathname}`
				) || '{}'
			) as { token?: string; thanksQuery?: string };
		} catch {
			return {} as { token?: string; thanksQuery?: string };
		}
	}, []);

	const goToThanks = () => {
		const thanksPath = window.location.pathname.replace(
			`/p/${variant}/`,
			'/p/thanks/'
		);
		const query = stored.thanksQuery || searchParams.toString();
		window.location.replace(`${thanksPath}${query ? `?${query}` : ''}`);
	};

	/** Recusa: upsell com downsell configurado desvia pra oferta alternativa. */
	const decline = () => {
		if (
			variant === 'upsell' &&
			downsell?.enabled &&
			downsell?.contentId &&
			stored.token
		) {
			const downsellPath = window.location.pathname.replace(
				'/p/upsell/',
				'/p/downsell/'
			);
			try {
				sessionStorage.setItem(
					`checkout-upsell:${downsellPath}`,
					JSON.stringify(stored)
				);
				const query =
					stored.thanksQuery || searchParams.toString();
				window.location.replace(
					`${downsellPath}${query ? `?${query}` : ''}`
				);
				return;
			} catch {
				// sem storage, segue direto pro obrigado
			}
		}
		goToThanks();
	};

	const charge = useMutation(paymentsApi.oneClickUpsell, {
		onSuccess: () => {
			trackBehavior(
				variant === 'downsell'
					? 'downsell_accepted'
					: 'upsell_accepted',
				undefined,
				String(upsell?.contentId ?? '')
			);
			setStatus('success');
			// Deixa o "adicionado ao pedido" visível por um instante.
			window.setTimeout(goToThanks, 1600);
		},
		onError: (error: unknown) => {
			setStatus('error');
			const message =
				(error as { response?: { data?: { message?: string } } })
					?.response?.data?.message ||
				'Não foi possível concluir. Você pode seguir sem a oferta.';
			setErrorMessage(message);
		}
	});

	// Sem token ou sem oferta configurada (acesso direto/expirado): não trava
	// o comprador — segue para o obrigado assim que a página resolver. No
	// preview do builder a página fica visível para edição, sem redirect.
	if (
		!isPreviewMode() &&
		page &&
		(!upsell?.enabled || !upsell?.contentId || !stored.token)
	) {
		goToThanks();
		return null;
	}

	const accept = () => {
		if (!stored.token || !upsell?.contentId || status === 'charging')
			return;
		setStatus('charging');
		charge.mutate({
			token: stored.token,
			contentId: upsell.contentId
		});
	};

	const accent =
		page?.settings?.confirmation?.buttonColor ||
		page?.settings?.accentColor ||
		undefined;

	return (
		<div className="thanks-page upsell-page">
			<div className="container">
				<div className="thanks-hero upsell-offer">
					<span className="upsell-badge">
						Oferta exclusiva pós-compra
					</span>
					<h1>
						{upsell?.title ||
							(variant === 'downsell'
								? 'Tudo bem! Que tal esta alternativa?'
								: 'Espere! Uma oferta para você')}
					</h1>
					{upsell?.mediaUrl && (
						<img
							className="upsell-media"
							src={upsell.mediaUrl}
							alt=""
						/>
					)}
					<p>
						{upsell?.message ||
							'Adicione esta oferta ao seu pedido com um clique — sem digitar o cartão de novo.'}
					</p>

					{status === 'success' ? (
						<p className="upsell-success">
							✓ Adicionado ao seu pedido!
						</p>
					) : (
						<>
							<button
								type="button"
								className="thanks-cta upsell-accept"
								style={{ backgroundColor: accent }}
								disabled={status === 'charging'}
								onClick={accept}
							>
								{status === 'charging'
									? 'Processando…'
									: upsell?.acceptLabel ||
									  'Sim, adicionar ao meu pedido'}
							</button>
							<button
								type="button"
								className="upsell-decline"
								disabled={status === 'charging'}
								onClick={() => {
									trackBehavior(
										variant === 'downsell'
											? 'downsell_declined'
											: 'upsell_declined',
										undefined,
										String(upsell?.contentId ?? '')
									);
									decline();
								}}
							>
								{upsell?.declineLabel ||
									'Não, obrigado — seguir sem a oferta'}
							</button>
							{status === 'error' && (
								<p className="upsell-error">{errorMessage}</p>
							)}
						</>
					)}
				</div>

				{/* Blocos livres do builder (page.upsell) — argumentos de
					venda abaixo do cartão da oferta. */}
				<PageBlocks
					page={
						page
							? { ...page, blocks: page[variant] ?? [] }
							: page
					}
					checkoutId={checkout?.id}
				/>
				{/* Área de solta no builder mesmo com a página vazia. */}
				{isPreviewMode() && !page?.[variant]?.length && (
					<div className="page-blocks" style={{ minHeight: 140 }} />
				)}
			</div>
		</div>
	);
};
